import L from 'leaflet';
import { Feature } from 'geojson';
import {
  calculateOrientationShiftedPosition,
  cellMarkersReprPolySizesPerZoom,
} from 'utils/Map/mapUtils';
import { LeafletExtensionNIBaseLayer } from './NIBaseClass/LeafletExtensionNIBaseLayer';

export default class LeafletShiftedPositionAutoResizeMarker extends L.CircleMarker {
  private initialLocation: [number, number];
  private orientationAngle: number;
  private shiftedIndex: number;
  private extendedLayerData?: Feature;
  private currentSizePx: number = 30;
  private parentLayer: LeafletExtensionNIBaseLayer;

  constructor(
    parentLayer: LeafletExtensionNIBaseLayer,
    extendedLayerData: Feature,
    initialLocation: [number, number],
    orientationAngle?: number,
    shiftedIndex?: number
  ) {
    // extract the layer definition default color in case need to use it
    let layerDefinitionLegendFirstColor = Object.values(
      parentLayer.niLayerOptions.layerDefinition.LayerProperties
        ?.LegendValueToColorMapping ?? {}
    )[0];
    const styleFromGeoJson = extendedLayerData['style'] || {}; // Default to empty if no style in feature

    super([0, 0], {
      stroke: true, // dnot draw border
      weight: 1, // line weight , used only for highlight
      color:
        styleFromGeoJson.stroke ||
        styleFromGeoJson.color ||
        styleFromGeoJson.strokeColor ||
        'black', // line color, used only for highlight
      radius: 15, // this is temporal since will be reset in the resize function
      opacity: 1,
      fillColor:
        styleFromGeoJson.fillColor ||
        styleFromGeoJson.color ||
        layerDefinitionLegendFirstColor ||
        'red', // line color, used only for highlight
      fillOpacity: 1,
    });

    this.initialLocation = initialLocation;
    this.orientationAngle = orientationAngle ?? 0;
    this.shiftedIndex = shiftedIndex ?? 0;
    this.extendedLayerData = extendedLayerData;
    this.parentLayer = parentLayer;

    const popupContent = `<strong>Layer:</strong> ${parentLayer.niLayerOptions.layerDefinition.LayerName}`;
    this.bindPopup(popupContent, {});
    this.on('pm:enable', (e: any) => e.target.pm.disable());
  }

  // Called when added to the map
  onAdd(map: L.Map) {
    // Call the parent class's onAdd method first
    super.onAdd(map);
    // Calculate and set triangle vertices
    this._resizeAndRelocate();
    this._map.on('zoomend', this._resizeAndRelocate, this);
    return this;
  }

  onRemove(map: L.Map) {
    this._map.off('zoomend', this._resizeAndRelocate, this);
    super.onRemove(map);
    return this;
  }

  _resizeAndRelocate() {
    const map = this._map;
    const zoomLevel = map.getZoom();

    // recalculate the center again based on zoom and the size per zoom
    this.currentSizePx =
      cellMarkersReprPolySizesPerZoom[zoomLevel] || this.currentSizePx;
    const cellShiftedCenterLatLng = calculateOrientationShiftedPosition(
      map,
      this.currentSizePx,
      this.initialLocation,
      this.orientationAngle,
      this.shiftedIndex
    );
    // recalculate marker size which is based on the triangle squeare size:
    let newCircleRadius = this.currentSizePx / (2 * Math.sqrt(3));

    this.setLatLng(cellShiftedCenterLatLng);
    this.setRadius(newCircleRadius);
  }
}
